import { Component, DestroyRef, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { TranslocoService } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { MonthPickerComponent, MonthPickerDisplayFormat } from "../../month-picker/month-picker.component";
import { fromTargetMonth, fromYearMonth, toTargetMonth, toYearMonth } from "../../month-picker/month-picker.utils";

/**
  Example usage:

  {
    key: "period",
    type: "monthYear",
    props: {
      label: { en: "Period", fi: "Kausi", no: "Period", sv: "Period" },  // Optional
      required: true,                                        // Optional
      placeholder: { en: "Select a month", fi: "Valitse kuukausi", no: "Välj en månad", sv: "Välj en månad" }, // Optional
      description: { en: "Pick a month", fi: "Valitse kuukausi", no: "Välj en månad", sv: "Välj en månad" },    // Optional
      displayFormat: "long",      // Optional, "long" (default, localized "July 2026") | "numeric" ("07/2026")
    }
  }

  Stored model value is a "YYYY-MM" string, e.g. "2026-07".

  ////////////

  JSON example:
  {
    "key": "period-month-year",
    "type": "monthYear",
    "props": {
      "label": {
        "en": "Period",
        "fi": "Kausi",
        "no": "Period",
        "sv": "Period"
      },
      "required": false,
      "description": {
        "en": "Pick a month",
        "fi": "Valitse kuukausi",
        "no": "Välj en månad",
        "sv": "Välj en månad"
      },
      "placeholder": {
        "en": "Select a month",
        "fi": "Valitse kuukausi",
        "no": "Välj en månad",
        "sv": "Välj en månad"
      },
      "displayFormat": "long"
    }
  }
*/
@Component({
  selector: "app-month-year",
  templateUrl: "./month-year.type.html",
  standalone: true,
  imports: [ReactiveFormsModule, MonthPickerComponent],
})
export class MonthYearComponent extends FieldType<FieldTypeConfig> implements OnInit {
  /**
   * The picker works in Dates, but this field's model value is a "YYYY-MM" string (it lives in the
   * process form's JSON), so the two are bridged here and kept in sync both ways.
   */
  dateControl = new FormControl<Date | null>(null);

  constructor(
    private translocoService: TranslocoService,
    private destroyRef: DestroyRef,
  ) {
    super();
  }

  get displayFormat(): MonthPickerDisplayFormat {
    return this.props["displayFormat"] === "numeric" ? "numeric" : "long";
  }

  ngOnInit(): void {
    this.dateControl.setValue(this.toDate(this.formControl.value), { emitEvent: false });

    this.dateControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((date) => {
      this.formControl.setValue(date ? toYearMonth(fromTargetMonth(date)) : null);
      this.formControl.markAsDirty();
    });

    this.formControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: string | null) => {
      const current = this.dateControl.value ? toYearMonth(fromTargetMonth(this.dateControl.value)) : null;
      if (value !== current) {
        this.dateControl.setValue(this.toDate(value), { emitEvent: false });
      }
    });
  }

  onBlur(): void {
    this.formControl.markAsTouched();
  }

  isFieldInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  getErrorMessage(): string {
    if (this.formControl.hasError("required")) {
      return this.translocoService.translate("app.formly-types.validationMessages.fieldRequired");
    }
    return this.translocoService.translate("app.formly-types.validationMessages.invalidDate");
  }

  private toDate(yearMonth: string | null | undefined): Date | null {
    const firstOfMonth = fromYearMonth(yearMonth);
    return firstOfMonth ? toTargetMonth(firstOfMonth) : null;
  }
}
